import { useState } from "react";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent } from "@shared/ui/card";
import { Search, User, Phone, MapPin, Mail } from "lucide-react";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useConsumerSearchWithExtraInfo } from "@features/callCenter/callCenterDashboard/hooks";
import { useTestSendTemplate } from "../../templates/hooks";
import { useParams } from "react-router-dom";

interface TestSendCommunicationProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  source: "template" | "recipient-list";
  templateName?: string;
  onSend?: (payload: any) => void;
}

const TestSendCommunication = ({
  open,
  onOpenChange,
  source,
  templateName,
  onSend,
}: TestSendCommunicationProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedConsumer, setSelectedConsumer] = useState<any | null>(null);
  const { id, name } = useParams();
  
  // Fetch consumers based on search
  const { data: consumers } = useConsumerSearchWithExtraInfo({
    remoteUtilityId: getRemoteUtilityId(),
    searchData: searchTerm,
    isExtraInfo: 1,
  });
  const { mutate: testSendTemplate } = useTestSendTemplate();

  // Form fields including test recipient email
  const communicationFormFields: FormField[] = [
    {
      name: "testRecipientEmail",
      type: "email",
      label: "Test Recipient Email",
      placeholder: "Enter test recipient email",
      required: true,
      className: "col-span-2 mt-3",
    },
  ];

  const [formData, setFormData] = useState({
    subject: "",
    testRecipientEmail: "",
  });

  const communicationForm = FormService.createForm({
    fields: communicationFormFields,
    validateOnMount: false,
    defaultValues: formData,
  });

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const filteredConsumers = consumers?.results;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-success/10 text-success border-success/20";
      case "overdue":
        return "bg-destructive/10 text-destructive border-destructive/20";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  const handleConsumerSelect = (consumer: any) => {
    setSelectedConsumer(consumer);
    setSearchTerm("");
  };

  const removeSelectedConsumer = () => {
    setSelectedConsumer(null);
  };

  const handleSendMessage = () => {
    const payload = {
      remote_utility_id: Number(getRemoteUtilityId()),
      template_id: Number(id), 
      recipient: [formData.testRecipientEmail], 
      test_data: {},
    };

    testSendTemplate(payload);
    if (onSend) onSend(payload);
    handleReset();
    onOpenChange(false);
  };

  const handleCancel = () => {
    handleReset();
    onOpenChange(false);
  };

  const handleReset = () => {
    setSearchTerm("");
    setSelectedConsumer(null);
    setFormData({
      subject: "",
      testRecipientEmail: "",
    });
    communicationForm.reset({
      subject: "",
      testRecipientEmail: "",
    });
  };

  // Get dialog title and description based on source
  const getDialogContent = () => {
    if (source === "template") {
      return {
        title: "Send Communication",
        description: `Send "${templateName}" to a specific recipient.`,
      };
    }
    return {
      title: "Send Communication",
      description: "Send communication to a specific recipient.",
    };
  };

  const { title, description } = getDialogContent();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Recipient Section */}
          <div className="space-y-3">
            <Label htmlFor="recipient" className="text-base font-semibold">
              Recipient <span className="text-destructive">*</span>
            </Label>

            {/* Selected Consumer Display */}
            {selectedConsumer ? (
              <Card className="border-2 border-primary/50 bg-primary/5">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-base">
                            {selectedConsumer.firstName}{" "}
                            {selectedConsumer.lastName}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {selectedConsumer.consumerNo}
                          </p>
                        </div>
                        <Badge
                          className={getStatusColor(
                            selectedConsumer.statusDisplay
                          )}
                        >
                          {selectedConsumer.statusDisplay}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          <span>{selectedConsumer.email || "No email"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          <span>{selectedConsumer.contactNumber}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span>
                          {selectedConsumer.territoryData?.service?.area},{" "}
                          {selectedConsumer.territoryData?.service?.subArea},{" "}
                          {selectedConsumer.territoryData?.service?.premise}
                        </span>
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={removeSelectedConsumer}
                      className="h-8 w-8 p-0"
                    >
                      ✕
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="recipient"
                    placeholder="Search by name, email, phone, or account number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Search hint */}
                {!searchTerm && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Type at least 2 characters to search...
                  </p>
                )}

                {/* Search Results */}
                {searchTerm.length >= 2 && (
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">
                      Search Results ({filteredConsumers?.length || 0} found)
                    </Label>

                    {filteredConsumers && filteredConsumers.length > 0 ? (
                      <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-2">
                        {filteredConsumers.map((consumer: any) => (
                          <Card
                            key={consumer.id}
                            className="cursor-pointer hover:bg-accent/50 transition-colors"
                            onClick={() => handleConsumerSelect(consumer)}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                    <User className="h-4 w-4 text-primary" />
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-sm">
                                      {consumer.firstName} {consumer.lastName}
                                    </h4>
                                    <p className="text-xs text-muted-foreground">
                                      {consumer.consumerNo} •{" "}
                                      {consumer.email || consumer.contactNumber}
                                    </p>
                                  </div>
                                </div>
                                <Badge
                                  className={getStatusColor(
                                    consumer.statusDisplay
                                  )}
                                >
                                  {consumer.statusDisplay}
                                </Badge>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 border rounded-lg">
                        <Search className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                        <p className="text-muted-foreground text-sm">
                          No consumers found matching your search
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            
          </div>

          {/* Test Recipient Email Field using DynamicForm */}
          {selectedConsumer && (
            <DynamicForm
              fields={communicationFormFields}
              form={communicationForm}
              onFieldChange={handleFieldChange}
              gridColumns={2}
              onSubmit={handleSendMessage}
              onReset={handleCancel}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                spacing: "comfortable",
                classes: {
                  form: "space-y-4",
                  input:
                    "w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-input",
                  label: "block text-sm font-medium mb-1",
                  error: "text-sm text-destructive mt-1",
                  container: "space-y-1",
                },
              }}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSendMessage}
            disabled={!selectedConsumer || !formData.testRecipientEmail}
          >
            Send Message
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TestSendCommunication;